/* eslint-disable @typescript-eslint/no-unused-vars */

import { Injectable } from '@angular/core';
import { authState, getAuth, getIdToken, idToken, UserCredential } from '@angular/fire/auth';
import { NbAuthIllegalTokenError, NbAuthResult, NbAuthStrategy } from '@nebular/auth';
import { from, Observable, of as observableOf } from 'rxjs';
import { catchError, map } from 'rxjs/operators';

// No `providedIn`: the class is abstract, and it emits a valid inherited factory rather
// than `ɵɵinvalidFactory`. Registering it at the root would turn `inject()` on the base
// type from a loud NullInjectorError into a silently constructed, unconfigured strategy.
@Injectable()
export abstract class NbFirebaseBaseStrategy extends NbAuthStrategy {
  protected afAuth = getAuth();
  protected idToken$ = idToken(this.afAuth);
  protected authState$ = authState(this.afAuth);
  protected newDefAuthRes = (
    module: string,
    success: boolean,
    options?: {
      response?: unknown;
      redirectIsSuccess?: boolean;
      errors?: unknown;
      messages?: unknown;
      token?: string;
    },
  ) =>
    new NbAuthResult(
      success,
      options?.response ? options?.response : null,
      this.getOption(`${module}.redirect.${options?.redirectIsSuccess === false ? 'failure' : 'success'}`),
      options?.errors ? options?.errors : [],
      options?.messages ? options?.messages : this.getOption(`${module}.defaultMessages`),
      options?.token ? this.createToken(options?.token) : undefined,
    );

  logout(): Observable<NbAuthResult> {
    const module = 'logout';
    return from(this.afAuth.signOut()).pipe(
      map(() => this.newDefAuthRes(module, true)),
      catchError((error) => this.processFailure(error, module)),
    );
  }

  register(_data?: unknown): Observable<NbAuthResult> {
    throw new Error(`'register' is not supported by '${this.constructor.name}', use 'authenticate'.`);
  }

  requestPassword(_data?: unknown): Observable<NbAuthResult> {
    throw new Error(`'requestPassword' is not supported by '${this.constructor.name}', use 'authenticate'.`);
  }

  resetPassword(_data: unknown = {}): Observable<NbAuthResult> {
    throw new Error(`'resetPassword' is not supported by '${this.constructor.name}', use 'authenticate'.`);
  }

  refreshToken(_data: unknown = {}): Observable<NbAuthResult> {
    throw new Error(`'refreshToken' is not supported by '${this.constructor.name}', use 'authenticate'.`);
  }

  protected processFailure(error: Error, module: string): Observable<NbAuthResult> {
    console.log('processFailure', { error, module }, this.options);

    const errorMessages = [];

    if (error instanceof NbAuthIllegalTokenError) {
      errorMessages.push(error.message);
    } else {
      errorMessages.push(this.getOption('errors.getter')(module, error, this.options));
    }

    return observableOf(
      new NbAuthResult(false, error, this.getOption(`${module}.redirect.failure`), errorMessages, []),
    );
  }

  protected processSuccess(response: UserCredential, module: string): Observable<NbAuthResult> {
    // console.log('processSuccess', { response, module }, this.options);
    return from(getIdToken(response.user)).pipe(
      map((token) => {
        // console.log('processSuccess', token, this.options);
        return this.newDefAuthRes(module, true, {
          messages: this.getOption('messages.getter')(module, response, this.options),
          response,
          token,
        });
      }),
    );
  }
}
